"use client";
import * as React from "react";
import { atom, useAtom } from "jotai";
import { authClient } from "@acme/auth/client";
import { trpcReactQueryApi } from "~/trpc/react";
import { ViewsConfig } from "@acme/core/config/views-config";
import {
  Pitch,
  PitchFormDraft,
  PitchSortBy,
  ToastMessage,
} from "@acme/entities";
import type {
  UsePitchWorkspace,
  UsePitchWorkspaceParams,
  UsePitchWorkspaceReturn,
} from "~/modules/main/hooks/use-pitch-workspace/use-pitch-workspace.interface";
/**
 * Global atoms for the Pitch Workspace domain.
 * These atoms ensure consistent state across multiple presenters/components.
 */
const teamIdAtom = atom<string | null>(null);
const teamNameAtom = atom<string | null>(null);
const searchQueryAtom = atom<string>("");
const sortByAtom = atom<PitchSortBy>(PitchSortBy.UPDATED_AT_DESC);
const isDrawerOpenAtom = atom<boolean>(false);
const isGeneratingAtom = atom<boolean>(false);
const generationProgressAtom = atom<number>(0);
const errorMessageAtom = atom<string | null>(null);
const toastsAtom = atom<ToastMessage[]>([]);
const selectedPitchIdAtom = atom<string | null>(null);
// If a pitch is selected but not present in the list (e.g., after a direct load by id),
// keep a local override until list includes it.
const selectedPitchOverrideAtom = atom<Pitch | null>(null);
// Remember last used form by pitch id for regeneration UX.
const lastFormByPitchIdAtom = atom<Record<string, PitchFormDraft>>({});
/**
 * Helper: produce a sorted copy of pitches according to sortBy enum.
 */
function sortPitches(pitches: Pitch[], sortBy: PitchSortBy): Pitch[] {
  const arr = [...pitches];
  switch (sortBy) {
    case PitchSortBy.TITLE_ASC:
      return arr.sort((a, b) => a.title.localeCompare(b.title));
    case PitchSortBy.TITLE_DESC:
      return arr.sort((a, b) => b.title.localeCompare(a.title));
    case PitchSortBy.UPDATED_AT_ASC:
      return arr.sort(
        (a, b) => a.updatedAt.getTime() - b.updatedAt.getTime(),
      );
    case PitchSortBy.UPDATED_AT_DESC:
    default:
      return arr.sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
      );
  }
}
/**
 * Map server-side error messages to user-friendly toasts/messages.
 */
function friendlyError(message?: string | null): string {
  if (!message) return "Something went wrong. Please try again.";
  if (message.includes("PITCH_SAVE_FAILED")) {
    return "We couldn't save the generated pitch. Please try again.";
  }
  if (message.includes("No active team found")) {
    return "No active team found. Please select a team to continue.";
  }
  if (message.includes("Invalid team context")) {
    return "You are not authorized to access this team.";
  }
  return message;
}
/**
 * Simple sleep utility for backoff delays.
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
/**
 * Hook Implementation
 */
export function usePitchWorkspace(
  params: UsePitchWorkspaceParams,
): UsePitchWorkspaceReturn {
  // Active organization (for team name) from auth client (reactive).
  const { data: activeOrganization } = authClient.useActiveOrganization();
  // Global team context
  const [teamId, setTeamId] = useAtom(teamIdAtom);
  const [, setTeamName] = useAtom(teamNameAtom);
  // Resolve a VALID teamId (team-scoped) via active member, not organization id.
  // This prevents FK violations against pitch.teamId foreign key.
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const activeMember = await authClient.organization.getActiveMember();
        if (!mounted) return;
        setTeamId(activeMember?.data?.teamId ?? null);
        // Use organization name for display; it remains helpful context in UI.
        setTeamName(activeOrganization?.name ?? null);
      } catch {
        if (!mounted) return;
        setTeamId(null);
        setTeamName(activeOrganization?.name ?? null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [setTeamId, setTeamName, activeOrganization?.name]);
  // UI state atoms
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [sortBy, setSortBy] = useAtom(sortByAtom);
  const [isDrawerOpen, setDrawerOpen] = useAtom(isDrawerOpenAtom);
  // Generation state atoms
  const [isGenerating, setIsGenerating] = useAtom(isGeneratingAtom);
  const [generationProgress, setGenerationProgress] =
    useAtom(generationProgressAtom);
  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);
  // Toasts
  const [toasts, setToasts] = useAtom(toastsAtom);
  // Selected pitch
  const [selectedPitchId, setSelectedPitchId] = useAtom(selectedPitchIdAtom);
  const [selectedPitchOverride, setSelectedPitchOverride] = useAtom(
    selectedPitchOverrideAtom,
  );
  // Last-used form payloads per pitch for regeneration
  const [lastFormByPitchId, setLastFormByPitchId] = useAtom(
    lastFormByPitchIdAtom,
  );

  // Track if we're inside a multi-attempt retry loop to prevent premature onSettled resets.
  const isRetryingRef = React.useRef<boolean>(false);

  // Initialize default sort by from params exactly once
  React.useEffect(() => {
    if (params?.initialSortBy) {
      setSortBy(params.initialSortBy);
    }
    // Run once on mount; do not re-run if params object is re-created by consumers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // TRPC utilities for invalidation and imperative fetching
  const utils = trpcReactQueryApi.useUtils();
  // Server-state: list of pitches for the active team (scoped server-side via validated teamId)
  const {
    data: serverPitches,
    isLoading: isLoadingPitches,
    isError: isListError,
  } = trpcReactQueryApi.main.pitches.list.useQuery(
    teamId ? { teamId } : ({} as any),
    {
      enabled: !!teamId, // only load when a valid team is available
      gcTime: 5 * 60 * 1000,
    },
  );
  // Compute filtered and sorted pitches locally as per the interface contract
  const filteredSortedPitches = React.useMemo(() => {
    const base = serverPitches ?? [];
    const filtered = searchQuery
      ? base.filter((p) =>
          p.title.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : base;
    return sortPitches(filtered, sortBy);
  }, [serverPitches, searchQuery, sortBy]);
  // Compute selected pitch from either list or override (e.g., when loaded by id)
  const selectedPitch: Pitch | null = React.useMemo(() => {
    if (!selectedPitchId) return null;
    const inList =
      filteredSortedPitches.find((p) => p.id === selectedPitchId) ?? null;
    return inList ?? selectedPitchOverride;
  }, [filteredSortedPitches, selectedPitchId, selectedPitchOverride]);
  // If the override pitch appears in the list (same id), clear the override to avoid duplication
  React.useEffect(() => {
    if (selectedPitchOverride && selectedPitchId) {
      const inList = (serverPitches ?? []).some(
        (p) => p.id === selectedPitchId,
      );
      if (inList) {
        setSelectedPitchOverride(null);
      }
    }
  }, [
    selectedPitchOverride,
    selectedPitchId,
    serverPitches,
    setSelectedPitchOverride,
  ]);
  // Local helpers for toast handling, wrapped with useCallback for stability
  const enqueueToast = React.useCallback(
    (message: string, type: ToastMessage["type"]) => {
      const toast: ToastMessage = {
        id: crypto.randomUUID(),
        message,
        type,
        createdAt: new Date(),
      };
      setToasts((prev) => [toast, ...prev].slice(0, 6));
    },
    [setToasts],
  );
  const dismissToast = React.useCallback(
    (id: string) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [setToasts],
  );
  // Mutations
  const createMutation = trpcReactQueryApi.main.pitches.create.useMutation({
    onSuccess: async (created) => {
      await utils.main.pitches.list.invalidate();
      setSelectedPitchId(created.id);
      enqueueToast("Pitch created", "success");
    },
    onError: (err) => {
      const msg = friendlyError((err as any)?.message);
      setErrorMessage(msg);
      enqueueToast(msg, "error");
    },
  });
  const updateTitleMutation =
    trpcReactQueryApi.main.pitches.updateTitle.useMutation({
      onSuccess: async (updated) => {
        await utils.main.pitches.list.invalidate();
        if (selectedPitch?.id === updated.id && selectedPitchOverride) {
          setSelectedPitchOverride({
            ...selectedPitchOverride,
            title: updated.title,
            updatedAt: updated.updatedAt,
          });
        }
        enqueueToast("Title updated", "success");
      },
      onError: (err) => {
        const msg = friendlyError((err as any)?.message);
        setErrorMessage(msg);
        enqueueToast(msg, "error");
      },
    });
  const updateBodyMutation =
    trpcReactQueryApi.main.pitches.updateBody.useMutation({
      onSuccess: async (updated) => {
        await utils.main.pitches.list.invalidate();
        if (selectedPitch?.id === updated.id) {
          // optimistic sync for details pane
          setSelectedPitchOverride(
            selectedPitch
              ? {
                  ...selectedPitch,
                  body: updated.body,
                  updatedAt: updated.updatedAt,
                }
              : updated,
          );
        }
        enqueueToast("Changes saved", "success");
      },
      onError: (err) => {
        const msg = friendlyError((err as any)?.message);
        setErrorMessage(msg);
        enqueueToast(msg, "error");
      },
    });
  const softDeleteMutation =
    trpcReactQueryApi.main.pitches.softDelete.useMutation({
      onSuccess: async (deleted) => {
        await utils.main.pitches.list.invalidate();
        if (selectedPitch?.id === deleted.id) {
          setSelectedPitchId(null);
          setSelectedPitchOverride(null);
        }
        enqueueToast("Pitch moved to trash", "success");
      },
      onError: (err) => {
        const msg = friendlyError((err as any)?.message);
        setErrorMessage(msg);
        enqueueToast(msg, "error");
      },
    });

  // Guard onSettled to avoid toggling isGenerating during internal retries
  const generateMutation =
    trpcReactQueryApi.main.pitches.generateFromForm.useMutation({
      onSuccess: async (persisted) => {
        await utils.main.pitches.list.invalidate();
        setSelectedPitchId(persisted.id);
        setSelectedPitchOverride(persisted);
        enqueueToast("Pitch generated", "success");
      },
      onError: (err) => {
        const msg = friendlyError((err as any)?.message);
        setErrorMessage(msg);
        enqueueToast(msg, "error");
      },
      onSettled: async () => {
        if (isRetryingRef.current) {
          // Skip resetting progress while retrying
          return;
        }
        setIsGenerating(false);
        setGenerationProgress(100);
        // small delay to reset progress indicator gracefully
        setTimeout(() => setGenerationProgress(0), 300);
      },
    });

  // Commands implementation
  const loadPitches = React.useCallback(async () => {
    await utils.main.pitches.list.invalidate();
  }, [utils.main.pitches.list]);

  const selectPitch = React.useCallback(
    async (pitchId: string) => {
      if (!teamId) {
        const msg = friendlyError("No active team found");
        setErrorMessage(msg);
        enqueueToast(msg, "error");
        return;
      }
      setSelectedPitchId(pitchId);
      // If not present in local server state, fetch imperatively and set override
      const inList = (serverPitches ?? []).some((p) => p.id === pitchId);
      if (!inList) {
        try {
          const fetched = await utils.main.pitches.byId.fetch({
            id: pitchId,
            teamId,
          });
          setSelectedPitchOverride(fetched);
        } catch (err) {
          const msg = friendlyError((err as any)?.message);
          setErrorMessage(msg);
          enqueueToast(msg, "error");
        }
      }
    },
    [
      serverPitches,
      setSelectedPitchId,
      setSelectedPitchOverride,
      setErrorMessage,
      utils.main.pitches.byId,
      teamId,
      enqueueToast,
    ],
  );

  const createPitch = React.useCallback(
    async (title: string) => {
      if (!teamId) {
        const msg = friendlyError("No active team found");
        setErrorMessage(msg);
        enqueueToast(msg, "error");
        return;
      }
      createMutation.mutate({ title, teamId });
    },
    [createMutation, teamId, enqueueToast, setErrorMessage],
  );

  const renamePitch = React.useCallback(
    async (pitchId: string, newTitle: string) => {
      if (!teamId) {
        const msg = friendlyError("No active team found");
        setErrorMessage(msg);
        enqueueToast(msg, "error");
        return;
      }
      updateTitleMutation.mutate({ id: pitchId, title: newTitle, teamId });
    },
    [updateTitleMutation, teamId, enqueueToast, setErrorMessage],
  );

  const softDeletePitch = React.useCallback(
    async (pitchId: string) => {
      if (!teamId) {
        const msg = friendlyError("No active team found");
        setErrorMessage(msg);
        enqueueToast(msg, "error");
        return;
      }
      softDeleteMutation.mutate({ id: pitchId, teamId });
    },
    [softDeleteMutation, teamId, enqueueToast, setErrorMessage],
  );

  const generatePitch = React.useCallback(
    async (form: PitchFormDraft) => {
      try {
        if (!teamId) {
          const msg = friendlyError("No active team found");
          setErrorMessage(msg);
          enqueueToast(msg, "error");
          return;
        }
        setIsGenerating(true);
        setErrorMessage(null);
        // Minimal but user-visible progress increments to communicate work
        setGenerationProgress(10);

        const existingPitchId = selectedPitch?.id ?? null;
        // Store form for regeneration for existing pitch immediately
        if (existingPitchId) {
          setLastFormByPitchId((prev) => ({
            ...prev,
            [existingPitchId]: form,
          }));
        }

        // Retry strategy: up to 3 attempts for PITCH_SAVE_FAILED with exponential backoff (200ms, 400ms)
        isRetryingRef.current = true;
        let lastErrorMessage: string | null = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            setGenerationProgress(10 + attempt * 20); // 30, 50, 70 visual progression
            const persisted = await generateMutation.mutateAsync({
              form: {
                productTitle: form.productTitle,
                productDescription: form.productDescription,
                targetCustomer: form.targetCustomer ?? null,
                customerPainPoint: form.customerPainPoint ?? null,
                additionalContext: form.additionalContext ?? null,
              },
              existingPitchId,
              teamId,
            });
            // Remember form for the persisted id (covers both create and update paths)
            setLastFormByPitchId((prev) => ({
              ...prev,
              [persisted.id]: form,
            }));
            setGenerationProgress(90);
            lastErrorMessage = null;
            break; // success, exit retry loop
          } catch (err) {
            const msg = (err as any)?.message ?? "";
            lastErrorMessage = msg;
            // Only retry for structured persistence failure
            if (msg.includes("PITCH_SAVE_FAILED") && attempt < 3) {
              await sleep(200 * Math.pow(2, attempt - 1)); // 200ms, 400ms
              continue;
            } else {
              // Non-retryable or out of attempts; surface friendly error
              const friendly = friendlyError(msg);
              setErrorMessage(friendly);
              enqueueToast(friendly, "error");
              break;
            }
          }
        }
        // If after retries we still have an error code, ensure UI shows it
        if (lastErrorMessage) {
          const friendly = friendlyError(lastErrorMessage);
          setErrorMessage(friendly);
          enqueueToast(friendly, "error");
        }
      } finally {
        // Allow the mutation's onSettled to finalize progress now
        isRetryingRef.current = false;
      }
    },
    [
      selectedPitch?.id,
      setIsGenerating,
      setErrorMessage,
      setGenerationProgress,
      setLastFormByPitchId,
      generateMutation,
      teamId,
      enqueueToast,
      isRetryingRef,
    ],
  );

  const regeneratePitch = React.useCallback(async () => {
    if (!selectedPitch?.id) {
      const msg = "No pitch selected to regenerate";
      setErrorMessage(msg);
      enqueueToast(msg, "error");
      return;
    }
    const lastForm = lastFormByPitchId[selectedPitch.id];
    if (!lastForm) {
      const msg = "Nothing to regenerate. Generate a pitch first.";
      setErrorMessage(msg);
      enqueueToast(msg, "error");
      return;
    }
    await generatePitch(lastForm);
  }, [
    selectedPitch?.id,
    lastFormByPitchId,
    generatePitch,
    enqueueToast,
    setErrorMessage,
  ]);

  const savePitchContent = React.useCallback(
    async (pitchId: string, newBody: string) => {
      if (!teamId) {
        const msg = friendlyError("No active team found");
        setErrorMessage(msg);
        enqueueToast(msg, "error");
        return;
      }
      // Optimistic update to make UI feel responsive
      if (selectedPitch?.id === pitchId) {
        const optimistic: Pitch = {
          ...(selectedPitch as Pitch),
          body: newBody,
          updatedAt: new Date(),
        };
        setSelectedPitchOverride(optimistic);
      }
      updateBodyMutation.mutate({ id: pitchId, body: newBody, teamId });
    },
    [
      selectedPitch,
      setSelectedPitchOverride,
      updateBodyMutation,
      teamId,
      enqueueToast,
      setErrorMessage,
    ],
  );

  return {
    // State
    teamId,
    teamName: activeOrganization?.name ?? null,
    pitches: filteredSortedPitches,
    selectedPitch: selectedPitch ?? null,
    searchQuery,
    sortBy,
    isDrawerOpen,
    isGenerating,
    generationProgress,
    errorMessage,
    toasts,
    createdAt: selectedPitch?.createdAt ?? null,
    updatedAt: selectedPitch?.updatedAt ?? null,
    // Mutations (local state)
    setDrawerOpen,
    setSearchQuery,
    setSortBy,
    enqueueToast,
    dismissToast,
    // Commands (server interactions or workflows)
    loadPitches,
    selectPitch,
    createPitch,
    renamePitch,
    softDeletePitch,
    generatePitch,
    regeneratePitch,
    savePitchContent,
  };
}